import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile, useToasts } from '@brg/ui';
import { Designer } from '@brg/api';
import { LayoutConst, useModal } from 'core';
import { Date32Icon } from 'shared/components/tagAdminProjectsSearchBar/date32';
import { Date24Icon } from 'shared/components/tagAdminProjectsSearchBar/date24';
import { MagicBoxRow } from 'shared/components/MagicBoxRow';
import {
  IConsultationLessonData,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';
import {
  calcMaxCapacity,
  calcMaxTimePerPerson,
  checkConsultationCapacityFit,
  getConsultationDurationMinutes,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/helpers';
import { ClockButton } from 'shared/modals/LessonScheduleModals/ClockButton';
import { FieldLabel } from 'shared/modals/LessonScheduleModals/FieldLabel';
import {
  Chip,
  Chips,
  ClearDayButton,
  CloseButton,
  DaysControls,
  Dropdown,
  DropdownFixedField,
  Field,
  FieldHint,
  Header,
  HeaderRow,
  ModalDatePicker,
  ModalWrapper,
  Row,
  Subtitle,
  TextInput,
  TimeWrapper,
  Title,
  WeekdayButton,
  WeekdaysRow,
} from 'shared/modals/LessonScheduleModals/styles';

type ConsultationDay = {
  start: string;
  end: string;
  location: string;
  teacherId: string;
  isInvitedTeacher: boolean;
};
const emptyConsultationDay = (): ConsultationDay => ({
  start: '', end: '', location: '', teacherId: '', isInvitedTeacher: false,
});
const snapshotDay = (v: Partial<ConsultationDay>): ConsultationDay => ({
  start: v.start ?? '',
  end: v.end ?? '',
  location: v.location ?? '',
  teacherId: v.teacherId ?? '',
  isInvitedTeacher: Boolean(v.isInvitedTeacher),
});
const capacityErrorByReason: Record<string, string> = {
  missing_time: 'lms.lessonsEditor.enterTimeError',
  invalid_time_range: 'lms.lessonsEditor.endTimeBeforeStartError',
  missing_capacity: 'lms.consultation.capacityRequired',
};

export interface ConsultationLessonModalProps {
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>;
  disciplineOptions: Array<{ value: string; label: string }>;
  themeOptions?: Array<{ value: string; label: string }>;
  selectedDisciplineId?: string;
  moduleTeachers: Designer<'Local'>[];
  getGroupName: (group: { groupId: string; name: Array<{ lang: string; name: string }> }) => string;
  isRecurring: boolean;
  lockTimeFields?: boolean;
  initialData?: Partial<IConsultationLessonData>;
  onSubmit: (data: IConsultationLessonData, onSuccess: () => void) => void;
}

export const ConsultationLessonModal: FC<ConsultationLessonModalProps> = ({
  courseStartDate,
  courseEndDate,
  courseGroups,
  disciplineOptions,
  themeOptions = [],
  selectedDisciplineId,
  moduleTeachers,
  getGroupName,
  isRecurring,
  lockTimeFields = false,
  initialData,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const isMobile = useIsMobile();
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);
  const [activeDay, setActiveDay] = useState(() => {
    const fromSelected = initialData?.selectedDays?.[0];
    if (fromSelected != null) return fromSelected;

    const date = initialData?.courseStartDate ?? courseStartDate;
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return date.getDay();
    }

    return 1;
  });

  const daysRef = useRef<Record<number, ConsultationDay>>(initialData?.daySchedules ?? {});
  const [dayResetKey, setDayResetKey] = useState(0);

  const { control, watch, setValue, getValues, handleSubmit } = useForm<IConsultationLessonData>({
    defaultValues: {
      disciplineId: initialData?.disciplineId ?? selectedDisciplineId,
      courseStartDate: initialData?.courseStartDate ?? courseStartDate,
      courseEndDate: initialData?.courseEndDate ?? courseEndDate,
      selectedDays: initialData?.selectedDays ?? [],
      start: initialData?.start ?? '',
      end: initialData?.end ?? '',
      capacity: initialData?.capacity ?? '',
      timePerPerson: initialData?.timePerPerson ?? null,
      format: initialData?.format ?? 'online',
      location: initialData?.location ?? '',
      registrationDaysBefore: initialData?.registrationDaysBefore ?? null,
      teacherId: initialData?.teacherId ?? '',
      isInvitedTeacher: initialData?.isInvitedTeacher ?? false,
      selectedGroups: initialData?.selectedGroups ?? [],
      selectedThemeId: initialData?.selectedThemeId,
    },
  });

  const formData = watch();
  const durationMinutes = useMemo(
    () => getConsultationDurationMinutes(formData.start, formData.end),
    [formData.start, formData.end],
  );
  const isTimeReady = durationMinutes != null;
  const prevTimeRef = useRef({
    start: initialData?.start ?? '',
    end: initialData?.end ?? '',
  });

  useEffect(() => {
    const prev = prevTimeRef.current;
    const startChanged = prev.start !== formData.start;
    const endChanged = prev.end !== formData.end;
    prevTimeRef.current = { start: formData.start, end: formData.end };

    if (!startChanged && !endChanged) return;
    if (getConsultationDurationMinutes(prev.start, prev.end) == null) return;

    setValue('capacity', '');
    setValue('timePerPerson', null);
  }, [formData.start, formData.end, setValue]);

  const handleCapacityChange = useCallback((rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '').replace(/^0+/, '');
    setValue('capacity', digits, { shouldDirty: true });

    if (!durationMinutes || lockTimeFields) return;

    const capacityNum = Number(digits);
    if (!digits || !Number.isFinite(capacityNum) || capacityNum <= 0) {
      setValue('timePerPerson', null, { shouldDirty: true });
      return;
    }

    setValue('timePerPerson', calcMaxTimePerPerson(durationMinutes, capacityNum), { shouldDirty: true });
  }, [durationMinutes, lockTimeFields, setValue]);

  const handleTimePerPersonChange = useCallback((rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '');
    if (!digits) {
      setValue('timePerPerson', null, { shouldDirty: true });
      setValue('capacity', '', { shouldDirty: true });
      return;
    }

    const minutes = Number(digits);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setValue('timePerPerson', null, { shouldDirty: true });
      setValue('capacity', '', { shouldDirty: true });
      return;
    }

    setValue('timePerPerson', minutes, { shouldDirty: true });

    if (!durationMinutes || lockTimeFields) return;

    const nextCapacity = calcMaxCapacity(durationMinutes, minutes);
    setValue('capacity', nextCapacity != null ? String(nextCapacity) : '', { shouldDirty: true });
  }, [durationMinutes, lockTimeFields, setValue]);

  const weekdayOptions = useMemo(() => [
    { value: 1, label: t('lms.lessonsEditor.weekDays.mon') },
    { value: 2, label: t('lms.lessonsEditor.weekDays.tue') },
    { value: 3, label: t('lms.lessonsEditor.weekDays.wed') },
    { value: 4, label: t('lms.lessonsEditor.weekDays.thu') },
    { value: 5, label: t('lms.lessonsEditor.weekDays.fri') },
    { value: 6, label: t('lms.lessonsEditor.weekDays.sat') },
    { value: 0, label: t('lms.lessonsEditor.weekDays.sun') },
  ], [t]);
  const registrationOptions = useMemo(
    () => Array.from({ length: 14 }, (_, index) => index + 1).map((value) => ({ value: String(value), label: t('lms.consultation.beforeDays', { count: value }) })),
    [t],
  );
  const formatOptions = useMemo(
    () => [
      { value: 'online', label: t('lms.consultation.online') },
      { value: 'offline', label: t('lms.consultation.offline') },
    ],
    [t],
  );
  const teacherOptions = useMemo(
    () => moduleTeachers.map((teacher) => ({
      value: teacher.designerId,
      label: [teacher.surName, teacher.name, teacher.middleName].filter(Boolean).join(' '),
    })),
    [moduleTeachers],
  );

  const applyDay = useCallback((entry: ConsultationDay) => {
    prevTimeRef.current = { start: entry.start, end: entry.end };
    setValue('start', entry.start);
    setValue('end', entry.end);
    setValue('location', entry.location);
    setValue('teacherId', entry.teacherId);
    setValue('isInvitedTeacher', entry.isInvitedTeacher);
    setDayResetKey((k) => k + 1);
  }, [setValue]);

  const persistDay = useCallback(() => {
    if (!isRecurring) return;
    daysRef.current = { ...daysRef.current, [activeDay]: snapshotDay(getValues()) };
  }, [activeDay, isRecurring, getValues]);

  const toggleDay = useCallback((day: number) => {
    if (isRecurring && day !== activeDay) {
      persistDay();
      applyDay(daysRef.current[day] ?? emptyConsultationDay());
    }
    setActiveDay(day);
    const selected = watch('selectedDays');
    if (!selected.includes(day)) setValue('selectedDays', [...selected, day]);
  }, [activeDay, applyDay, isRecurring, persistDay, setValue, watch]);

  const clearActiveDay = useCallback(() => {
    setValue('selectedDays', watch('selectedDays').filter((d) => d !== activeDay));
    if (!isRecurring) return;
    const { [activeDay]: _removed, ...rest } = daysRef.current;
    daysRef.current = rest;
    applyDay(emptyConsultationDay());
  }, [activeDay, applyDay, isRecurring, setValue, watch]);

  const toggleGroup = useCallback((groupId: string) => {
    const selected = watch('selectedGroups');
    setValue(
      'selectedGroups',
      selected.includes(groupId) ? selected.filter((id) => id !== groupId) : [...selected, groupId],
    );
  }, [setValue, watch]);

  const submit = useCallback((data: IConsultationLessonData) => {
    const activeSnap = snapshotDay(data);
    const daySchedules = isRecurring
      ? { ...daysRef.current, [activeDay]: activeSnap }
      : undefined;
    const selectedDays = data.selectedDays ?? [];
    const dayList = isRecurring
      ? selectedDays.map((day) => daySchedules![day] ?? emptyConsultationDay())
      : [activeSnap];

    const commonMissing = (isRecurring && !data.disciplineId)
      || !data.courseStartDate
      || (isRecurring && (!data.courseEndDate || !selectedDays.length))
      || (!isRecurring && !data.selectedThemeId)
      || !String(data.capacity || '').trim()
      || !data.timePerPerson
      || data.timePerPerson <= 0
      || !data.format
      || data.registrationDaysBefore == null;
    const dayMissing = dayList.some((s) => !s.start || !s.end || !s.location.trim() || !s.teacherId);

    if (commonMissing || dayMissing) {
      toasts.error(t('lms.consultation.fillRequiredFields'));
      return;
    }

    for (const s of dayList) {
      const check = checkConsultationCapacityFit({
        start: s.start, end: s.end, capacity: data.capacity, timePerPerson: data.timePerPerson,
      });
      if (check.ok) continue;
      const key = capacityErrorByReason[check.reason];
      toasts.error(key ? t(key) : t('lms.consultation.capacityOverflow', {
        duration: check.durationMinutes ?? 0,
        capacity: Number(data.capacity),
        maxSlots: check.maxSlots ?? 0,
        minutes: data.timePerPerson,
      }));
      return;
    }

    const payload = isRecurring
      ? { ...data, daySchedules }
      : {
          ...data,
          courseEndDate: data.courseStartDate ? new Date(data.courseStartDate.getTime()) : null,
          selectedDays: data.courseStartDate ? [data.courseStartDate.getDay()] : data.selectedDays,
        };

    onSubmit(payload, () => modal.hide());
  }, [activeDay, isRecurring, modal, onSubmit, t, toasts]);

  return (
    <ModalWrapper>
      <Header>
        <HeaderRow>
          <Title>{t('lms.consultation.title')}</Title>
          <CloseButton onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </HeaderRow>
        <Subtitle>{t('lms.consultation.subtitle')}</Subtitle>
      </Header>

      {isRecurring && (
        <Field>
          <FieldLabel required>{t('lms.consultation.discipline')}</FieldLabel>
          <Dropdown>
            <Controller
              name="disciplineId"
              control={control}
              render={({ field }) => (
                <CommonInput
                  type={INPUT_TYPES.dropdown}
                  orientation="column"
                  options={disciplineOptions}
                  value={disciplineOptions.find((option) => option.value === field.value)}
                  onChange={(option: any) => field.onChange(option?.value)}
                  placeholder={t('lms.consultation.discipline')}
                />
              )}
            />
          </Dropdown>
        </Field>
      )}

      {isRecurring && (
        <>
          <Row>
            <Field>
              <FieldLabel required>{t('lms.lessonsEditor.startDateLabel')}</FieldLabel>
              <Controller name="courseStartDate" control={control} render={({ field }) => (
                <ModalDatePicker
                  // @ts-ignore
                  value={field.value}
                  onChange={(date: Date | null) => field.onChange(date)}
                  format="dd.MM.yyyy"
                  calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                  clearIcon={null}
                  locale={i18n.language}
                />
              )} />
            </Field>
            <Field>
              <FieldLabel required>{t('lms.lessonsEditor.endDateLabel')}</FieldLabel>
              <Controller name="courseEndDate" control={control} render={({ field }) => (
                <ModalDatePicker
                  // @ts-ignore
                  value={field.value}
                  onChange={(date: Date | null) => field.onChange(date)}
                  format="dd.MM.yyyy"
                  calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                  clearIcon={null}
                  locale={i18n.language}
                  minDate={formData.courseStartDate || undefined}
                />
              )} />
            </Field>
          </Row>

          <DaysControls>
            <Field>
              <FieldLabel required>{t('lms.lessonsEditor.selectDayLabel')}</FieldLabel>
              <WeekdaysRow>
                {weekdayOptions.map((day) => (
                  <WeekdayButton
                    key={day.value}
                    type="button"
                    $active={activeDay === day.value}
                    $filled={formData.selectedDays.includes(day.value)}
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </WeekdayButton>
                ))}
              </WeekdaysRow>
            </Field>
            <ClearDayButton type="button" onClick={clearActiveDay}>
              {t('lms.consultation.clearDay', {
                day: weekdayOptions.find((day) => day.value === activeDay)?.label,
              })}
            </ClearDayButton>
          </DaysControls>
        </>
      )}

      {!isRecurring && (
        <Row>
          <DropdownFixedField>
            <FieldLabel required>{t('lms.lessonsEditor.chooseTheme')}</FieldLabel>
            <Dropdown>
              <Controller
                name="selectedThemeId"
                control={control}
                render={({ field }) => (
                  <CommonInput
                    type={INPUT_TYPES.dropdown}
                    orientation="column"
                    options={themeOptions}
                    value={themeOptions.find((option) => option.value === field.value)}
                    onChange={(option: any) => field.onChange(option?.value)}
                    placeholder={t('lms.lessonsEditor.chooseTheme')}
                  />
                )}
              />
            </Dropdown>
          </DropdownFixedField>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.lessonDateLabel')}</FieldLabel>
            <Controller name="courseStartDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                disabled={lockTimeFields}
                onChange={(date: Date | null) => {
                  if (lockTimeFields) return;
                  field.onChange(date);
                  setValue('courseEndDate', date ? new Date(date.getTime()) : null);
                }}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
              />
            )} />
          </Field>
        </Row>
      )}

      <Row>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.startTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="start" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  startRef.current = element;
                }}
                type="time"
                value={field.value}
                disabled={lockTimeFields}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => !lockTimeFields && startRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.endTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="end" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  endRef.current = element;
                }}
                type="time"
                min={formData.start || undefined}
                value={field.value}
                disabled={lockTimeFields}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => !lockTimeFields && endRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
      </Row>

      <FlexBox direction="column" gy={8} width="100%">
        <Row $alignItems="end">
          <Field>
            <FieldLabel required>{t('lms.consultation.capacity')}</FieldLabel>
            <Controller name="capacity" control={control} render={({ field }) => (
              <TextInput
                inputMode="numeric"
                disabled={!isTimeReady}
                value={field.value}
                onChange={(event) => handleCapacityChange(event.target.value)}
                title={!isTimeReady ? t('lms.consultation.fillTimeFirst') : undefined}
              />
            )} />
          </Field>
          <Field>
            <FieldLabel required>{t('lms.consultation.timePerPerson')}</FieldLabel>
            <Controller name="timePerPerson" control={control} render={({ field }) => (
              <TextInput
                inputMode="numeric"
                disabled={!isTimeReady || lockTimeFields}
                value={field.value ?? ''}
                onChange={(event) => handleTimePerPersonChange(event.target.value)}
                title={!isTimeReady ? t('lms.consultation.fillTimeFirst') : undefined}
              />
            )} />
          </Field>
        </Row>
        {!isTimeReady && (
          <FieldHint>{t('lms.consultation.fillTimeFirst')}</FieldHint>
        )}
      </FlexBox>

      <Row>
        <Field>
          <FieldLabel required>{t('lms.consultation.format')}</FieldLabel>
          <Dropdown>
            <Controller name="format" control={control} render={({ field }) => (
              <CommonInput
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={formatOptions}
                value={formatOptions.find((option) => option.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value || 'online')}
              />
            )} />
          </Dropdown>
        </Field>
        <Field>
          <FieldLabel required>
            {formData.format === 'online' ? t('lms.consultation.lessonLink') : t('lms.consultation.room')}
          </FieldLabel>
          <Controller name="location" control={control} render={({ field }) => (
            <TextInput placeholder={t('lms.lessonsEditor.enterData')} {...field} />
          )} />
        </Field>
      </Row>

      <Row $columns={2}>
        <Field>
          <FieldLabel required>{t('lms.consultation.registration')}</FieldLabel>
          <Dropdown>
            <Controller name="registrationDaysBefore" control={control} render={({ field }) => (
              <CommonInput
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={registrationOptions}
                value={registrationOptions.find((option) => option.value === String(field.value))}
                onChange={(option: any) => field.onChange(option?.value ? Number(option.value) : null)}
              />
            )} />
          </Dropdown>
        </Field>
        <Field>
          <FieldLabel required>{t('lms.consultation.teacher')}</FieldLabel>
          <Dropdown>
            <Controller name="teacherId" control={control} render={({ field }) => (
              <CommonInput
                key={`teacher-${activeDay}-${dayResetKey}`}
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={teacherOptions}
                value={teacherOptions.find((option) => option.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value || '')}
                placeholder={t('lms.lessonsEditor.chooseTeacher')}
              />
            )} />
          </Dropdown>
          <Controller name="isInvitedTeacher" control={control} render={({ field }) => (
            <MagicBoxRow
              className="checkbox module_checkbox"
              checked={field.value}
              label={t('lms.consultation.invitedTeacher')}
              onClick={() => field.onChange(!field.value)}
              isTeamEdit
            />
          )} />
        </Field>
      </Row>

      <Field>
        <FieldLabel>{t('lms.lessonsEditor.groupsLabel')}</FieldLabel>
        <Chips>
          {courseGroups.map((group) => {
            const isSelected = formData.selectedGroups.includes(group.groupId);
            return (
              <Chip
                key={group.groupId}
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => toggleGroup(group.groupId)}
              >
                <span>{getGroupName(group)}</span>
              </Chip>
            );
          })}
        </Chips>
      </Field>

      <FlexBox justify="flex-end">
        <BrgButton fullWidth variant={ButtonVariant.Filled} onClick={handleSubmit(submit)}>
          {t('lms.consultation.submit')}
        </BrgButton>
      </FlexBox>
    </ModalWrapper>
  );
};
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile, useToasts } from '@brg/ui';
import { Designer } from '@brg/api';
import { LayoutConst, useModal } from 'core';
import { Date32Icon } from 'shared/components/tagAdminProjectsSearchBar/date32';
import { Date24Icon } from 'shared/components/tagAdminProjectsSearchBar/date24';
import { MagicBoxRow } from 'shared/components/MagicBoxRow';
import {
  IConsultationLessonData,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';
import {
  calcMaxCapacity,
  calcMaxTimePerPerson,
  checkConsultationCapacityFit,
  getConsultationDurationMinutes,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/helpers';
import { ClockButton } from 'shared/modals/LessonScheduleModals/ClockButton';
import { FieldLabel } from 'shared/modals/LessonScheduleModals/FieldLabel';
import {
  Chip,
  Chips,
  ClearDayButton,
  CloseButton,
  DaysControls,
  Dropdown,
  DropdownFixedField,
  Field,
  FieldHint,
  Header,
  HeaderRow,
  ModalDatePicker,
  ModalWrapper,
  Row,
  Subtitle,
  TextInput,
  TimeWrapper,
  Title,
  WeekdayButton,
  WeekdaysRow,
} from 'shared/modals/LessonScheduleModals/styles';

type ConsultationDay = {
  start: string;
  end: string;
  location: string;
  teacherId: string;
  isInvitedTeacher: boolean;
};
const emptyConsultationDay = (): ConsultationDay => ({
  start: '', end: '', location: '', teacherId: '', isInvitedTeacher: false,
});
const snapshotDay = (v: Partial<ConsultationDay>): ConsultationDay => ({
  start: v.start ?? '',
  end: v.end ?? '',
  location: v.location ?? '',
  teacherId: v.teacherId ?? '',
  isInvitedTeacher: Boolean(v.isInvitedTeacher),
});
const capacityErrorByReason: Record<string, string> = {
  missing_time: 'lms.lessonsEditor.enterTimeError',
  invalid_time_range: 'lms.lessonsEditor.endTimeBeforeStartError',
  missing_capacity: 'lms.consultation.capacityRequired',
};

export interface ConsultationLessonModalProps {
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>;
  disciplineOptions: Array<{ value: string; label: string }>;
  themeOptions?: Array<{ value: string; label: string }>;
  selectedDisciplineId?: string;
  moduleTeachers: Designer<'Local'>[];
  getGroupName: (group: { groupId: string; name: Array<{ lang: string; name: string }> }) => string;
  isRecurring: boolean;
  lockTimeFields?: boolean;
  initialData?: Partial<IConsultationLessonData>;
  onSubmit: (data: IConsultationLessonData, onSuccess: () => void) => void;
}

export const ConsultationLessonModal: FC<ConsultationLessonModalProps> = ({
  courseStartDate,
  courseEndDate,
  courseGroups,
  disciplineOptions,
  themeOptions = [],
  selectedDisciplineId,
  moduleTeachers,
  getGroupName,
  isRecurring,
  lockTimeFields = false,
  initialData,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const isMobile = useIsMobile();
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);
  const [activeDay, setActiveDay] = useState(() => {
    const fromSelected = initialData?.selectedDays?.[0];
    if (fromSelected != null) return fromSelected;

    const date = initialData?.courseStartDate ?? courseStartDate;
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return date.getDay();
    }

    return 1;
  });

  const daysRef = useRef<Record<number, ConsultationDay>>(initialData?.daySchedules ?? {});
  const [dayResetKey, setDayResetKey] = useState(0);

  const { control, watch, setValue, getValues, handleSubmit } = useForm<IConsultationLessonData>({
    defaultValues: {
      disciplineId: initialData?.disciplineId ?? selectedDisciplineId,
      courseStartDate: initialData?.courseStartDate ?? courseStartDate,
      courseEndDate: initialData?.courseEndDate ?? courseEndDate,
      selectedDays: initialData?.selectedDays ?? [],
      start: initialData?.start ?? '',
      end: initialData?.end ?? '',
      capacity: initialData?.capacity ?? '',
      timePerPerson: initialData?.timePerPerson ?? null,
      format: initialData?.format ?? 'online',
      location: initialData?.location ?? '',
      registrationDaysBefore: initialData?.registrationDaysBefore ?? null,
      teacherId: initialData?.teacherId ?? '',
      isInvitedTeacher: initialData?.isInvitedTeacher ?? false,
      selectedGroups: initialData?.selectedGroups ?? [],
      selectedThemeId: initialData?.selectedThemeId,
    },
  });

  const formData = watch();
  const durationMinutes = useMemo(
    () => getConsultationDurationMinutes(formData.start, formData.end),
    [formData.start, formData.end],
  );
  const isTimeReady = durationMinutes != null;
  const prevTimeRef = useRef({
    start: initialData?.start ?? '',
    end: initialData?.end ?? '',
  });

  useEffect(() => {
    const prev = prevTimeRef.current;
    const startChanged = prev.start !== formData.start;
    const endChanged = prev.end !== formData.end;
    prevTimeRef.current = { start: formData.start, end: formData.end };

    if (!startChanged && !endChanged) return;
    if (getConsultationDurationMinutes(prev.start, prev.end) == null) return;

    setValue('capacity', '');
    setValue('timePerPerson', null);
  }, [formData.start, formData.end, setValue]);

  const handleCapacityChange = useCallback((rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '').replace(/^0+/, '');
    setValue('capacity', digits, { shouldDirty: true });

    if (!durationMinutes || lockTimeFields) return;

    const capacityNum = Number(digits);
    if (!digits || !Number.isFinite(capacityNum) || capacityNum <= 0) {
      setValue('timePerPerson', null, { shouldDirty: true });
      return;
    }

    setValue('timePerPerson', calcMaxTimePerPerson(durationMinutes, capacityNum), { shouldDirty: true });
  }, [durationMinutes, lockTimeFields, setValue]);

  const handleTimePerPersonChange = useCallback((rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '');
    if (!digits) {
      setValue('timePerPerson', null, { shouldDirty: true });
      setValue('capacity', '', { shouldDirty: true });
      return;
    }

    const minutes = Number(digits);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setValue('timePerPerson', null, { shouldDirty: true });
      setValue('capacity', '', { shouldDirty: true });
      return;
    }

    setValue('timePerPerson', minutes, { shouldDirty: true });

    if (!durationMinutes || lockTimeFields) return;

    const nextCapacity = calcMaxCapacity(durationMinutes, minutes);
    setValue('capacity', nextCapacity != null ? String(nextCapacity) : '', { shouldDirty: true });
  }, [durationMinutes, lockTimeFields, setValue]);

  const weekdayOptions = useMemo(() => [
    { value: 1, label: t('lms.lessonsEditor.weekDays.mon') },
    { value: 2, label: t('lms.lessonsEditor.weekDays.tue') },
    { value: 3, label: t('lms.lessonsEditor.weekDays.wed') },
    { value: 4, label: t('lms.lessonsEditor.weekDays.thu') },
    { value: 5, label: t('lms.lessonsEditor.weekDays.fri') },
    { value: 6, label: t('lms.lessonsEditor.weekDays.sat') },
    { value: 0, label: t('lms.lessonsEditor.weekDays.sun') },
  ], [t]);
  const registrationOptions = useMemo(
    () => Array.from({ length: 14 }, (_, index) => index + 1).map((value) => ({ value: String(value), label: t('lms.consultation.beforeDays', { count: value }) })),
    [t],
  );
  const formatOptions = useMemo(
    () => [
      { value: 'online', label: t('lms.consultation.online') },
      { value: 'offline', label: t('lms.consultation.offline') },
    ],
    [t],
  );
  const teacherOptions = useMemo(
    () => moduleTeachers.map((teacher) => ({
      value: teacher.designerId,
      label: [teacher.surName, teacher.name, teacher.middleName].filter(Boolean).join(' '),
    })),
    [moduleTeachers],
  );

  const applyDay = useCallback((entry: ConsultationDay) => {
    prevTimeRef.current = { start: entry.start, end: entry.end };
    setValue('start', entry.start);
    setValue('end', entry.end);
    setValue('location', entry.location);
    setValue('teacherId', entry.teacherId);
    setValue('isInvitedTeacher', entry.isInvitedTeacher);
    setDayResetKey((k) => k + 1);
  }, [setValue]);

  const persistDay = useCallback(() => {
    if (!isRecurring) return;
    daysRef.current = { ...daysRef.current, [activeDay]: snapshotDay(getValues()) };
  }, [activeDay, isRecurring, getValues]);

  const toggleDay = useCallback((day: number) => {
    if (isRecurring && day !== activeDay) {
      persistDay();
      applyDay(daysRef.current[day] ?? emptyConsultationDay());
    }
    setActiveDay(day);
    const selected = watch('selectedDays');
    if (!selected.includes(day)) setValue('selectedDays', [...selected, day]);
  }, [activeDay, applyDay, isRecurring, persistDay, setValue, watch]);

  const clearActiveDay = useCallback(() => {
    setValue('selectedDays', watch('selectedDays').filter((d) => d !== activeDay));
    if (!isRecurring) return;
    const { [activeDay]: _removed, ...rest } = daysRef.current;
    daysRef.current = rest;
    applyDay(emptyConsultationDay());
  }, [activeDay, applyDay, isRecurring, setValue, watch]);

  const toggleGroup = useCallback((groupId: string) => {
    const selected = watch('selectedGroups');
    setValue(
      'selectedGroups',
      selected.includes(groupId) ? selected.filter((id) => id !== groupId) : [...selected, groupId],
    );
  }, [setValue, watch]);

  const submit = useCallback((data: IConsultationLessonData) => {
    const activeSnap = snapshotDay(data);
    const daySchedules = isRecurring
      ? { ...daysRef.current, [activeDay]: activeSnap }
      : undefined;
    const selectedDays = data.selectedDays ?? [];
    const dayList = isRecurring
      ? selectedDays.map((day) => daySchedules![day] ?? emptyConsultationDay())
      : [activeSnap];

    const commonMissing = (isRecurring && !data.disciplineId)
      || !data.courseStartDate
      || (isRecurring && (!data.courseEndDate || !selectedDays.length))
      || (!isRecurring && !data.selectedThemeId)
      || !String(data.capacity || '').trim()
      || !data.timePerPerson
      || data.timePerPerson <= 0
      || !data.format
      || data.registrationDaysBefore == null;
    const dayMissing = dayList.some((s) => !s.start || !s.end || !s.location.trim() || !s.teacherId);

    if (commonMissing || dayMissing) {
      toasts.error(t('lms.consultation.fillRequiredFields'));
      return;
    }

    for (const s of dayList) {
      const check = checkConsultationCapacityFit({
        start: s.start, end: s.end, capacity: data.capacity, timePerPerson: data.timePerPerson,
      });
      if (check.ok) continue;
      const key = capacityErrorByReason[check.reason];
      toasts.error(key ? t(key) : t('lms.consultation.capacityOverflow', {
        duration: check.durationMinutes ?? 0,
        capacity: Number(data.capacity),
        maxSlots: check.maxSlots ?? 0,
        minutes: data.timePerPerson,
      }));
      return;
    }

    const payload = isRecurring
      ? { ...data, daySchedules }
      : {
          ...data,
          courseEndDate: data.courseStartDate ? new Date(data.courseStartDate.getTime()) : null,
          selectedDays: data.courseStartDate ? [data.courseStartDate.getDay()] : data.selectedDays,
        };

    onSubmit(payload, () => modal.hide());
  }, [activeDay, isRecurring, modal, onSubmit, t, toasts]);

  return (
    <ModalWrapper>
      <Header>
        <HeaderRow>
          <Title>{t('lms.consultation.title')}</Title>
          <CloseButton onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </HeaderRow>
        <Subtitle>{t('lms.consultation.subtitle')}</Subtitle>
      </Header>

      {isRecurring && (
        <Field>
          <FieldLabel required>{t('lms.consultation.discipline')}</FieldLabel>
          <Dropdown>
            <Controller
              name="disciplineId"
              control={control}
              render={({ field }) => (
                <CommonInput
                  type={INPUT_TYPES.dropdown}
                  orientation="column"
                  options={disciplineOptions}
                  value={disciplineOptions.find((option) => option.value === field.value)}
                  onChange={(option: any) => field.onChange(option?.value)}
                  placeholder={t('lms.consultation.discipline')}
                />
              )}
            />
          </Dropdown>
        </Field>
      )}

      {isRecurring && (
        <>
          <Row>
            <Field>
              <FieldLabel required>{t('lms.lessonsEditor.startDateLabel')}</FieldLabel>
              <Controller name="courseStartDate" control={control} render={({ field }) => (
                <ModalDatePicker
                  // @ts-ignore
                  value={field.value}
                  onChange={(date: Date | null) => field.onChange(date)}
                  format="dd.MM.yyyy"
                  calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                  clearIcon={null}
                  locale={i18n.language}
                />
              )} />
            </Field>
            <Field>
              <FieldLabel required>{t('lms.lessonsEditor.endDateLabel')}</FieldLabel>
              <Controller name="courseEndDate" control={control} render={({ field }) => (
                <ModalDatePicker
                  // @ts-ignore
                  value={field.value}
                  onChange={(date: Date | null) => field.onChange(date)}
                  format="dd.MM.yyyy"
                  calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                  clearIcon={null}
                  locale={i18n.language}
                  minDate={formData.courseStartDate || undefined}
                />
              )} />
            </Field>
          </Row>

          <DaysControls>
            <Field>
              <FieldLabel required>{t('lms.lessonsEditor.selectDayLabel')}</FieldLabel>
              <WeekdaysRow>
                {weekdayOptions.map((day) => (
                  <WeekdayButton
                    key={day.value}
                    type="button"
                    $active={activeDay === day.value}
                    $filled={formData.selectedDays.includes(day.value)}
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </WeekdayButton>
                ))}
              </WeekdaysRow>
            </Field>
            <ClearDayButton type="button" onClick={clearActiveDay}>
              {t('lms.consultation.clearDay', {
                day: weekdayOptions.find((day) => day.value === activeDay)?.label,
              })}
            </ClearDayButton>
          </DaysControls>
        </>
      )}

      {!isRecurring && (
        <Row>
          <DropdownFixedField>
            <FieldLabel required>{t('lms.lessonsEditor.chooseTheme')}</FieldLabel>
            <Dropdown>
              <Controller
                name="selectedThemeId"
                control={control}
                render={({ field }) => (
                  <CommonInput
                    type={INPUT_TYPES.dropdown}
                    orientation="column"
                    options={themeOptions}
                    value={themeOptions.find((option) => option.value === field.value)}
                    onChange={(option: any) => field.onChange(option?.value)}
                    placeholder={t('lms.lessonsEditor.chooseTheme')}
                  />
                )}
              />
            </Dropdown>
          </DropdownFixedField>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.lessonDateLabel')}</FieldLabel>
            <Controller name="courseStartDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                disabled={lockTimeFields}
                onChange={(date: Date | null) => {
                  if (lockTimeFields) return;
                  field.onChange(date);
                  setValue('courseEndDate', date ? new Date(date.getTime()) : null);
                }}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
              />
            )} />
          </Field>
        </Row>
      )}

      <Row>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.startTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="start" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  startRef.current = element;
                }}
                type="time"
                value={field.value}
                disabled={lockTimeFields}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => !lockTimeFields && startRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.endTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="end" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  endRef.current = element;
                }}
                type="time"
                min={formData.start || undefined}
                value={field.value}
                disabled={lockTimeFields}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => !lockTimeFields && endRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
      </Row>

      <FlexBox direction="column" gy={8} width="100%">
        <Row $alignItems="end">
          <Field>
            <FieldLabel required>{t('lms.consultation.capacity')}</FieldLabel>
            <Controller name="capacity" control={control} render={({ field }) => (
              <TextInput
                inputMode="numeric"
                disabled={!isTimeReady}
                value={field.value}
                onChange={(event) => handleCapacityChange(event.target.value)}
                title={!isTimeReady ? t('lms.consultation.fillTimeFirst') : undefined}
              />
            )} />
          </Field>
          <Field>
            <FieldLabel required>{t('lms.consultation.timePerPerson')}</FieldLabel>
            <Controller name="timePerPerson" control={control} render={({ field }) => (
              <TextInput
                inputMode="numeric"
                disabled={!isTimeReady || lockTimeFields}
                value={field.value ?? ''}
                onChange={(event) => handleTimePerPersonChange(event.target.value)}
                title={!isTimeReady ? t('lms.consultation.fillTimeFirst') : undefined}
              />
            )} />
          </Field>
        </Row>
        {!isTimeReady && (
          <FieldHint>{t('lms.consultation.fillTimeFirst')}</FieldHint>
        )}
      </FlexBox>

      <Row>
        <Field>
          <FieldLabel required>{t('lms.consultation.format')}</FieldLabel>
          <Dropdown>
            <Controller name="format" control={control} render={({ field }) => (
              <CommonInput
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={formatOptions}
                value={formatOptions.find((option) => option.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value || 'online')}
              />
            )} />
          </Dropdown>
        </Field>
        <Field>
          <FieldLabel required>
            {formData.format === 'online' ? t('lms.consultation.lessonLink') : t('lms.consultation.room')}
          </FieldLabel>
          <Controller name="location" control={control} render={({ field }) => (
            <TextInput placeholder={t('lms.lessonsEditor.enterData')} {...field} />
          )} />
        </Field>
      </Row>

      <Row $columns={2}>
        <Field>
          <FieldLabel required>{t('lms.consultation.registration')}</FieldLabel>
          <Dropdown>
            <Controller name="registrationDaysBefore" control={control} render={({ field }) => (
              <CommonInput
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={registrationOptions}
                value={registrationOptions.find((option) => option.value === String(field.value))}
                onChange={(option: any) => field.onChange(option?.value ? Number(option.value) : null)}
              />
            )} />
          </Dropdown>
        </Field>
        <Field>
          <FieldLabel required>{t('lms.consultation.teacher')}</FieldLabel>
          <Dropdown>
            <Controller name="teacherId" control={control} render={({ field }) => (
              <CommonInput
                key={`teacher-${activeDay}-${dayResetKey}`}
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={teacherOptions}
                value={teacherOptions.find((option) => option.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value || '')}
                placeholder={t('lms.lessonsEditor.chooseTeacher')}
              />
            )} />
          </Dropdown>
          <Controller name="isInvitedTeacher" control={control} render={({ field }) => (
            <MagicBoxRow
              className="checkbox module_checkbox"
              checked={field.value}
              label={t('lms.consultation.invitedTeacher')}
              onClick={() => field.onChange(!field.value)}
              isTeamEdit
            />
          )} />
        </Field>
      </Row>

      <Field>
        <FieldLabel>{t('lms.lessonsEditor.groupsLabel')}</FieldLabel>
        <Chips>
          {courseGroups.map((group) => {
            const isSelected = formData.selectedGroups.includes(group.groupId);
            return (
              <Chip
                key={group.groupId}
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => toggleGroup(group.groupId)}
              >
                <span>{getGroupName(group)}</span>
              </Chip>
            );
          })}
        </Chips>
      </Field>

      <FlexBox justify="flex-end">
        <BrgButton fullWidth variant={ButtonVariant.Filled} onClick={handleSubmit(submit)}>
          {t('lms.consultation.submit')}
        </BrgButton>
      </FlexBox>
    </ModalWrapper>
  );
};
